import {ChangeEvent, ReactElement, useState, useEffect} from "react";
import {Avatar, CssBaseline, Box, Typography, Container, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, TextField, Button, Alert} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import {useSearchParams} from "react-router-dom";
import {Payment} from "./types";

export default function PaymentInfo(): ReactElement {
    const [searchParams] = useSearchParams();
    const [anonId, setAnonId] = useState<number | undefined>(undefined);
    const [payment, setPayment] = useState<Payment | undefined>();
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState("");
    const [showError, setShowError] = useState<boolean>(false);

    useEffect(() => {
        setAnonId(Number(searchParams.get("anonId")));
    }, []);

    useEffect(() => {
        anonId && loadPaymentInfo();
    }, [anonId]);
    
    const loadPaymentInfo = async () => {
        setLoading(true);
        setShowError(false);
        fetch("https://advances.vsb.cz" + "/eps/v1/paymentInfo?anonId=" + anonId, {
            method: 'GET',
        }).then((response) => {
            if(Boolean(response.ok)) {
                return response.json();
            }
            throw new Error("The specified payment could not be found.")
        })
            .then((payment: Payment) => {
                setLoading(false);
                setPayment(payment);
            })
            .catch((error) => {
                setLoading(false);
                setShowError(true);
            });
    };
    
    
    return (
        <Container sx={{textAlign: "center"}} component="main" maxWidth="xs">
            <CssBaseline />
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                    <PaymentIcon />
                </Avatar>
                <Typography component="h1" variant="h5" sx={{paddingBottom: "20px"}}>
                   Payment information
                </Typography>
                {showError && <Alert severity="error">The specified Payment ID could not be found.</Alert>}
                <Box>
                    {Boolean(anonId) && loading  && <CircularProgress />}
                    {payment ?
                        <>
                            <Typography variant={"subtitle1"} sx={{paddingBottom: "10px"}}>Please, write down the Payment ID to identify your payment.</Typography>
                            <TableContainer component={Paper}>
                                <Table sx={{minWidth: 650}} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 600}} align="center">Payment ID</TableCell>
                                            <TableCell sx={{fontWeight: 600}} align="center">Amount</TableCell>
                                            <TableCell sx={{fontWeight: 600}} align="center">Time of payment</TableCell>
                                            <TableCell sx={{fontWeight: 600}} align="center">Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                            <TableRow
                                                key={payment.anon_id}
                                                sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                            >
                                                <TableCell component="th" align="center" scope="row">
                                                    {payment.anon_id.toString()}
                                                </TableCell>
                                                <TableCell align="center">{payment.amount} EUR</TableCell>
                                                <TableCell align="center">{payment.timestamp ? new Date(payment.timestamp).toLocaleString() : "Not available"}</TableCell>
                                                <TableCell align="center">{payment.is_returned ? <span style={{color: "orange"}}>Returned</span> : (payment.is_paid ? <span style={{color: "green"}}>Paid</span> : <span style={{color: "red"}}>Unpaid</span>)}</TableCell>
                                            </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                        : 
                        <>
                            <Typography variant={"subtitle1"}>Please, specify your Payment ID</Typography>
                            <TextField
                                margin="dense"
                                id="payment-id"
                                label={"Payment ID"}
                                type="text"
                                fullWidth
                                value={value}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value)}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={!value}
                                onClick={() => setAnonId(Number(value))}
                            >
                                Confirm
                            </Button>
                        </>
                    }
                </Box>
            </Box>
        </Container>
    );
}